import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import {
    Button,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import useApprovalEligibleAssignees from "../../hooks/useApprovalEligibleAssignees";

const AssignModal = ({
    t,
    isOpen,
    toggle,
    softwareId,
    requirementId,
    title,
    currentAssigneeId,
    loading = false,
    onConfirm,
}) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const { users, isLoading } = useApprovalEligibleAssignees({
        softwareId,
        requirementId,
        enabled: isOpen && Boolean(requirementId),
        t,
    });
    const options = useMemo(
        () =>
            users.map((user) => ({
                value: String(user.id),
                label: user.name,
            })),
        [users]
    );

    useEffect(() => {
        if (!isOpen) {
            setSelectedUser(null);
            return;
        }

        if (currentAssigneeId == null) {
            return;
        }

        const current = options.find(
            (option) => option.value === String(currentAssigneeId)
        );

        if (current) {
            setSelectedUser(current);
        }
    }, [currentAssigneeId, isOpen, options]);

    const handleConfirm = () => {
        if (!selectedUser?.value) {
            return;
        }

        onConfirm({ userId: selectedUser.value });
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
                {currentAssigneeId
                    ? t("Change assignee")
                    : t("Assign")}
            </ModalHeader>
            <ModalBody>
                <Label className="form-label" for="required-approval-assignee">
                    {t("Assigned To")}
                </Label>
                <Select
                    inputId="required-approval-assignee"
                    classNamePrefix="required-approvals-assignee"
                    options={options}
                    value={selectedUser}
                    isDisabled={isLoading || loading}
                    isLoading={isLoading}
                    isSearchable
                    isClearable
                    placeholder={
                        isLoading
                            ? t("Loading...")
                            : t("Select a user")
                    }
                    noOptionsMessage={() => t("No eligible users.")}
                    menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={{
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: 2000,
                        }),
                    }}
                    filterOption={(option, input) =>
                        String(option.label || "")
                            .toLowerCase()
                            .includes(String(input || "").toLowerCase())
                    }
                    onChange={setSelectedUser}
                />
                {title ? (
                    <p className="text-muted font-size-13 mt-3 mb-0">
                        {t("Assign an eligible user to {{title}}.", {
                            title,
                        })}
                    </p>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    type="button"
                    onClick={toggle}
                    disabled={loading}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    color="primary"
                    type="button"
                    onClick={handleConfirm}
                    disabled={loading || isLoading || !selectedUser?.value}
                >
                    {loading ? (
                        <span className="spinner-border spinner-border-sm me-2" />
                    ) : null}
                    {currentAssigneeId
                        ? t("Change assignee")
                        : t("Assign")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(AssignModal);
